import { Shape, StickyNote, TextBlock } from "shared/datamodel/schemas";
import { newGroupId } from "frontend/canvas-designer-new/elements/canvas-elements-utils";
import consts from "shared/consts";
import { IRect, Point } from "utils/math-utils";
import Konva from "konva";
import { Reflect } from "@workcanvas/reflect/client";
import { M } from "shared/datamodel/mutators";
import { ReadTransaction } from "@workcanvas/reflect";
import { canvasElementPrefix } from "shared/util/utils";

export type DynamicElement = Shape | StickyNote;

const TOP_LEFT_ALIGNED_ELEMENTS = new Set([
  consts.CANVAS_ELEMENTS.STICKY_NOTE,
  consts.SHAPES.RECT,
  consts.SHAPES.RECT_ROUNDED,
]);

export function isElementCentered(element: DynamicElement): boolean {
  return !TOP_LEFT_ALIGNED_ELEMENTS.has(element.type as never);
}

/**
 * Get the content rectangle of an element, where dynamic texts can be placed.
 * Takes into account the differences in calculation between element types.
 */
export function getDynamicElementRect(element: DynamicElement): IRect {
  const width = element.width * (element.scaleX ?? 1);
  const height = element.height * (element.scaleY ?? 1);

  // Sticky notes and some old shapes are aligned to top left.
  if (isElementCentered(element)) {
    return { x: element.x - width / 2, y: element.y - height / 2, width, height };
  }

  return { x: element.x, y: element.y, width, height };
}

/**
 * Returns the width of a field based on its text value.
 * Optionally provide a max width which will be returned even when value is undefined.
 */
export function getFieldSize(fieldValue?: string, maxWidth?: number, fontSize?: number) {
  const defaultHeight = (fontSize ?? consts.DEFAULTS.FONTSIZE) * consts.LINE_HEIGHT;
  if (!fieldValue && !maxWidth) return { width: 0, height: defaultHeight }; // Should never happen.
  if (!fieldValue && maxWidth) return { width: maxWidth, height: defaultHeight };
  const margin = 10;

  const text = new Konva.Text({
    text: fieldValue,
    fontSize: fontSize ?? consts.DEFAULTS.FONTSIZE,
    fontFamily: consts.DEFAULTS.FONT,
    lineHeight: consts.LINE_HEIGHT,
    fontStyle: "300",
    ellipsis: false,
    wrap: "word",
    perfectDrawEnabled: false,
  });
  const width = Math.min(text.width() + margin, maxWidth ?? Number.POSITIVE_INFINITY);
  const height = text.height();

  return { width, height };
}

/**
 * Returns the text for a field.
 * @param elementId The element ID.
 * @param fieldId Custom field ID (Currently unsupported)
 * @param integrationData Integration data (Currently supported)
 */
export function getFieldText(
  elementId: string,
  fieldId?: string,
  integrationData?: { integrationId: string; columnId: string; itemId: string }
) {
  if (integrationData) {
    const { integrationId, columnId, itemId } = integrationData;
    return `\${${elementId}:integration:${integrationId}:${itemId}:${columnId}}`;
  }
  return `\${${elementId}:dataLayer.values.${fieldId}}`;
}

/**
 * Returns the group ID and history for a field.
 * @param element The element to get the group ID and history for.
 * @returns The group ID and history for the field. Also returns a boolean indicating whether the element should be updated after the field is added.
 */
export function getGroupIdForNewField(element: DynamicElement) {
  const derivedGroupId = `derived:${newGroupId()}`;
  let { groupId, groupHistory = [] } = element;
  let updateParentGroup = true;

  if (groupId && !groupId.startsWith("derived:")) {
    // Element was part of a group. We need the text block
    // to keep that group and save the derived group in its history.
    // NOTE: Ordering is important. Derived groups go first.
    groupHistory = [derivedGroupId, ...groupHistory];
  } else if (groupId && groupId.split(":")[0] === "derived") {
    // Element was part of a derived group.
    // Add the new text to the group while keeping the element as is.
    updateParentGroup = false;
  } else {
    // Element was not part of a group. Group it with the text block.
    groupId = derivedGroupId;
    groupHistory = [];
  }

  return { groupId, groupHistory, updateParentGroup };
}

/**
 * Returns the position for new fields on an element.
 * Does not check for available space.
 */
export function getFieldPosition(element: DynamicElement) {
  if (isElementCentered(element)) {
    return { x: element.x, y: element.y };
  }

  const padding = 10;
  return { x: element.x + padding, y: element.y + padding };
}

/**
 * Utility to find the next available position for a new field on an element,
 * based on the existing positions of fields.
 * @param positions - The existing positions of text fields on the element.
 * @param parent - The parent element.
 */
export function findNextAvailableFieldPosition(
  positions: { y: number; height: number }[],
  parent: DynamicElement,
  newFieldHeight: number
): Point {
  const parentRect = getDynamicElementRect(parent);

  // Filter out blocks that are not within the parent element's Y axis
  const filtered = positions.filter(
    (position) => position.y >= parentRect.y && position.y <= parentRect.y + parentRect.height
  );

  if (isElementCentered(parent)) {
    return findAvailableFieldPositionFromCenter(filtered, parentRect, newFieldHeight);
  }

  return findAvailableFieldPositionFromCorner(filtered, parentRect, newFieldHeight);
}

function findAvailableFieldPositionFromCorner(
  existingTextPositions: { y: number; height: number }[],
  parentRect: IRect,
  newFieldHeight: number
) {
  const padding = 10;
  const x = parentRect.x + padding;
  const startY = parentRect.y + padding;
  const endY = parentRect.y + parentRect.height - padding;

  const sorted = [...existingTextPositions].sort((a, b) => a.y - b.y);

  // Check gap at the top
  if (existingTextPositions.length === 0 || Math.ceil(sorted[0].y - startY) >= newFieldHeight) {
    return { x, y: startY };
  }

  // Check gaps between blocks
  for (let index = 0; index < existingTextPositions.length - 1; index++) {
    const gapStart = sorted[index].y + sorted[index].height;
    const gapEnd = sorted[index + 1].y;
    if (Math.ceil(gapEnd - gapStart) >= newFieldHeight) {
      return { x, y: gapStart };
    }
  }

  // Check gap at the bottom
  const lastBlock = sorted[sorted.length - 1];
  const gapStart = lastBlock.y + lastBlock.height;
  if (Math.ceil(endY - gapStart) >= newFieldHeight) {
    return { x, y: gapStart };
  }

  return { x, y: endY };
}

function findAvailableFieldPositionFromCenter(
  positions: { y: number; height: number }[],
  parentRect: IRect,
  newFieldHeight: number
) {
  const padding = 10;
  const x = parentRect.x + parentRect.width / 2;
  const centerY = parentRect.y + parentRect.height / 2;
  const startY = parentRect.y + padding;
  const endY = parentRect.y + parentRect.height - padding;

  const sortedPositions = [...positions].sort((a, b) => a.y - b.y);
  let offset = 0;

  // This loop breaks when the gap is found, or when hitting elemnt bounds.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    // Check below center
    const belowY = centerY + offset;
    if (belowY + newFieldHeight <= endY) {
      const gapFound = !sortedPositions.some((pos) => {
        const blockEnd = pos.y + pos.height;
        return belowY >= pos.y && belowY <= blockEnd;
      });
      if (gapFound) {
        return { x, y: belowY };
      }
    }

    // Check above center
    const aboveY = centerY - offset - newFieldHeight;
    if (aboveY >= startY) {
      const gapFound = !sortedPositions.some((pos) => {
        const blockEnd = pos.y + pos.height;
        return aboveY >= pos.y && aboveY <= blockEnd;
      });
      if (gapFound) {
        return { x, y: aboveY };
      }
    }

    offset += newFieldHeight;

    // If we've gone beyond the parent's bounds, return the center.
    if (aboveY < startY && belowY + newFieldHeight > endY) {
      return { x, y: startY };
    }
  }
}

export async function getDerivedTextBlockPositions(elementId: string, reflect?: Reflect<M>) {
  const positions = await reflect?.query(async function (tx: ReadTransaction) {
    const prefix = `${canvasElementPrefix}textBlock-`;
    let allTextBlocks = (await tx.scan({ prefix }).values().toArray()) as TextBlock[];
    allTextBlocks = allTextBlocks.filter((textBlock) => {
      if (textBlock?.hidden) return false;

      const text = textBlock.text;
      return (text ?? "").startsWith(`\${${elementId}:integration:`);
    });

    // Get positions and heights of all text blocks
    return allTextBlocks.map((textBlock) => ({
      y: textBlock.y,
      height: textBlock.height * (textBlock.scaleY ?? 1),
    }));
  });

  return positions;
}
